"use client";

import type { AirdropFarmingSuggestPairsResponse } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

/**
 * Fetches pair quality scores from the backend for a given exchange.
 * Returns scored pairs ranked by orderbook quality (spread, whale dominance, depth, symmetry).
 */
export function useSuggestPairs({ exchangeId }: { exchangeId: string }) {
  const { auth } = useHubUser();
  const apiHelper = useApi();

  const { data, isLoading } = useQuery<AirdropFarmingSuggestPairsResponse>({
    queryKey: [QueryKeys.AIRDROP_FARMING_SUGGEST_PAIRS, exchangeId],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AIRDROP_FARMING_SUGGEST_PAIRS, {
        queryParams: { exchangeId },
      }),
    enabled: !!exchangeId,
    staleTime: 60_000,
    // Retry every 5s while cache is empty (worker takes up to 3 min on fresh startup),
    // then settle to 2 min once we have data.
    refetchInterval: (query) => (query.state.data?.pairs?.length ? 120_000 : 5_000),
  });

  return {
    pairScores: data?.pairs ?? [],
    isLoadingScores: isLoading,
  };
}
